import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate } from "@/lib/helpers";
import { ArrowLeft, MapPin, Users, Trash2, ClipboardList } from "lucide-react";
import { toast } from "sonner";

export default function ClassroomDetail() {
  const { classroomId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isStaff = user?.role === "admin" || user?.role === "office_staff";
  const canViewRoster = isStaff || user?.role === "instructor";
  const [classroom, setClassroom] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/classrooms/${classroomId}`);
      setClassroom(res.data);
    } catch {
      toast.error("Could not load classroom");
    } finally {
      setLoading(false);
    }
  }, [classroomId]);

  useEffect(() => {
    load();
  }, [load]);

  const unenroll = async (studentId) => {
    try {
      await api.delete(`/classrooms/${classroomId}/enroll/${studentId}`);
      toast.success("Student unenrolled");
      load();
    } catch {
      toast.error("Could not unenroll");
    }
  };

  if (loading || !classroom) return <Skeleton className="h-96 rounded-[var(--radius)]" />;

  return (
    <div className="space-y-6" data-testid="classroom-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="classroom-detail-back-button">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold">{classroom.class_name}</h1>
          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="h-3.5 w-3.5" /> {classroom.location}</p>
        </div>
        {canViewRoster && (
          <Button variant="outline" onClick={() => navigate(`/classrooms/${classroomId}/roster`)} data-testid="view-roster-button">
            <ClipboardList className="h-4 w-4 mr-2" /> View Roster
          </Button>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="rounded-[var(--radius)] border bg-card p-4">
          <p className="text-xs text-muted-foreground">Instructor</p>
          <p className="font-medium">{classroom.instructor_name}</p>
        </div>
        <div className="rounded-[var(--radius)] border bg-card p-4">
          <p className="text-xs text-muted-foreground">Seats</p>
          <p className="font-medium">{classroom.remaining_seats}/{classroom.capacity} remaining</p>
        </div>
        <div className="rounded-[var(--radius)] border bg-card p-4">
          <p className="text-xs text-muted-foreground">Time</p>
          <p className="font-medium">{classroom.start_time} - {classroom.end_time}</p>
        </div>
      </div>

      <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-3">
        <h2 className="font-semibold font-heading">Session Dates</h2>
        <div className="flex flex-wrap gap-2">
          {(classroom.dates || []).map((d) => (
            <span key={d} className="text-xs bg-secondary rounded-full px-3 py-1">{formatDate(d)}</span>
          ))}
        </div>
      </div>

      {(classroom.parking_instructions || classroom.items_to_bring) && (
        <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-2 text-sm">
          {classroom.parking_instructions && <p><span className="text-muted-foreground">Parking: </span>{classroom.parking_instructions}</p>}
          {classroom.items_to_bring && <p><span className="text-muted-foreground">Bring: </span>{classroom.items_to_bring}</p>}
        </div>
      )}

      <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-3">
        <h2 className="font-semibold font-heading flex items-center gap-2"><Users className="h-4 w-4" /> Enrolled Students ({classroom.students?.length || 0})</h2>
        {(!classroom.students || classroom.students.length === 0) ? (
          <p className="text-sm text-muted-foreground">No students enrolled yet.</p>
        ) : (
          <div className="divide-y">
            {classroom.students.map((s) => (
              <div key={s.id} className="flex items-center justify-between py-2" data-testid="classroom-enrolled-student-row">
                <button onClick={() => navigate(`/students/${s.id}`)} className="text-sm hover:text-[hsl(var(--role-accent))]">{s.full_name}</button>
                {isStaff && (
                  <Button size="sm" variant="ghost" onClick={() => unenroll(s.id)} data-testid="classroom-unenroll-button">
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
